import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { useIsPc, useRecommendQuestion, useRedirectChat } from "@/src/hooks";

import { EdgenModalCreateAble } from "../EdgenModal";
import { SCOPE_MAP } from "@/src/const";
import { cn } from "utils";
import { encodeString } from "@/src/utils/stringEncoder";
import { memo } from "react";
import { useParams } from "next/navigation";

export const sourceMap = {
  "Fundamental analysis": "fa",
  "Technical analysis": "ta",
  "What It Is": "introduction",
} as any;

export const chatTypeMap = {
  stock: SCOPE_MAP.STOCK_CHAT,
  crypto: SCOPE_MAP.TOKEN_CHAT,
};
/**
 * ASK组件属性接口
 */
interface ASKProps {
  /** 容器自定义类名 */
  className?: string;
  symbol: string;
  isPollToken?: boolean;
  isStock?: boolean;
  onChange?: (value: string) => void;
  // 股票id
  stockId?: string;
}

type IFeature = {
  title?: string;
  prompt?: string;
  prompt_translation?: string;
  description?: string;
  language?: string;
  icon?: string;
};

interface ItemProps {
  className?: string;
  onClick?: () => void;
  titleClass?: string;
  title?: string;
  downIconClass?: string;
  icon?: string;
  dataTrackingScope?: string;
}

const Item: React.FC<ItemProps> = ({
  className,
  onClick,
  titleClass,
  title,
  icon,
  dataTrackingScope,
  downIconClass,
}) => {
  return (
    <div
      data-openreplay-label="TokenDetails · ASKList · div"
      className={cn(
        "flex-1 shrink-0 flex justify-center items-center gap-1 py-[5px] px-2 rounded-[4px] border-[0.75px] border-border_white_transparent20 cursor-pointer group/ask-item",
        className,
      )}
      data-tracking-scope={dataTrackingScope}
      onClick={onClick}
    >
      {/* 图标 */}
      {icon && (
        <div className="flex items-center justify-center w-4 h-4 rounded-full">
          <Icon
            name={icon}
            color={ospThemes.colors.content_brand_primary}
            aria-hidden="true"
            size={16}
          />
        </div>
      )}

      {/* 问题文本 */}
      <span
        className={cn(
          "title_s text-nowrap text-content_secondary group-hover/ask-item:text-content_primary",
          titleClass,
        )}
      >
        {title}
      </span>
      <Icon
        name="chevron-down"
        size={12}
        className={cn("text-content_primary md:!hidden", downIconClass)}
      />
    </div>
  );
};

interface ListProps {
  className?: string;
  data?: IFeature[];
  onClose?: () => void;
  onClick?: (data: IFeature) => void;
}

const List: React.FC<ListProps> = ({ onClose, onClick, data, className }) => {
  return (
    <div
      className={cn("flex flex-col gap-4 px-4 pt-8 pb-4 relative", className)}
    >
      <button
        data-openreplay-label="Close Button"
        id="btn-close-tokendetails-asklist-7"
        onClick={onClose}
        className="w-5 h-5 rounded-full flex items-center justify-center absolute right-3 top-3"
        aria-label="Close"
      >
        <Icon
          name="close"
          size={20}
          className="text-content_tertiary hover:text-content_secondary"
        />
      </button>
      {data?.map((item, index) => {
        return (
          <div
            data-openreplay-label="TokenDetails · ASKList · div"
            key={index}
            className="flex flex-col"
            onClick={() => onClick?.(item)}
          >
            <Item
              title={item.title}
              icon={item.icon}
              className="border-none p-0 justify-start"
              titleClass="first:shiny-text body_bold_m text-content_primary"
              downIconClass="!hidden"
            />
            <p className="body_s text-content_tertiary ml-5 truncate">
              {item.description}
            </p>
          </div>
        );
      })}
    </div>
  );
};

const openAskListModal = (props: Omit<ListProps, "onClose" | "onClick">) => {
  return new Promise<IFeature | null>((resolve) => {
    EdgenModalCreateAble.call({
      children: (
        <List
          data-openreplay-label="TokenDetails · ASKList · List"
          onClose={() => {
            EdgenModalCreateAble.end(false);
            resolve(null);
          }}
          onClick={(data) => {
            EdgenModalCreateAble.end(false);
            resolve(data);
          }}
          {...props}
        />
      ),
      containerClassName: "!md:w-[400px] !md:rounded-2xl",
      contentStyle: {
        borderRadius: 16,
        backgroundColor: ospThemes.colors.background_solid_secondary,
        width: "100%",
        padding: 0,
      },
      titleStyle: {
        minHeight: 0,
      },
      showClose: false,
      destroyOnClose: true,
    });
  });
};

/**
 * ASK组件 - 显示常见问题列表
 */
const ASKList = memo(
  ({
    className,
    symbol,
    isPollToken,
    isStock,
    onChange,
    stockId,
  }: ASKProps) => {
    const params = useParams();
    const [isPC] = useIsPc();
    const { handleCheckChatVaild } = useRedirectChat();
    const { id = "" } = params;
    const type = isStock ? "stock" : "crypto";
    const queryParams = {
      [`scopes[${chatTypeMap[type]}]`]: 20,
    };
    const { questions } = useRecommendQuestion(queryParams, {
      enabled: true,
      staleTime: 1000 * 60 * 5,
      key: chatTypeMap[type],
    });
    // 处理问题点击事件
    const handleQuestionClick = (realQuestion: any) => {
      const source = sourceMap[realQuestion?.key] || "chat";
      const replaceStr = isStock ? "{ticker}" : "{token}";
      const question = realQuestion.prompt.replace(replaceStr, symbol);
      const cacheQuestion = realQuestion.prompt_translation.replace(
        replaceStr,
        symbol,
      );
      onChange?.(cacheQuestion); // 使用多语言输入

      const signPrefix = isStock ? "stock_" : isPollToken ? "pool_" : "token_";
      const signId = isStock ? stockId : id;
      handleCheckChatVaild({
        question: cacheQuestion,
        cacheQuestion: question,
        sign: `${signPrefix}${encodeString(`${source}:symbol#${symbol}+id#${signId}`)}`,
        senderContext: true,
      });
    };

    const handleClick = async () => {
      const res = await openAskListModal({
        data: questions,
      });
      if (res) {
        handleQuestionClick(res);
      }
    };

    return (
      <div
        className={cn(
          "flex flex-row gap-2 overflow-x-auto no-scrollbar",
          className,
        )}
      >
        {!isPC && (
          <Item
            data-openreplay-label={questions?.[0]?.title}
            title={questions?.[0]?.title}
            icon={questions?.[0]?.icon}
            onClick={handleClick}
            className="mr-3"
            titleClass="shiny-text"
          />
        )}
        {isPC &&
          questions?.map((item, index) => {
            return (
              <Item
                data-openreplay-label={item?.title}
                key={index}
                dataTrackingScope={`${item.title}-${type}-recommend-question-${symbol || stockId}`}
                icon={item?.icon}
                title={item?.title}
                className="last:mr-2 first:gradient-border-detail-sender"
                onClick={() => handleQuestionClick(item)}
                titleClass={index === 0 ? "shiny-text" : undefined}
              />
            );
          })}
      </div>
    );
  },
);

ASKList.displayName = "ASKList";

export { ASKList };
